import {
  collection,
  deleteDoc,
  doc,
  FieldValue,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { QRCode, QRCodeForm, QRCodeStats } from '../types';

const QR_COLLECTION = 'qr-codes';

export class QRService {
  private static requireUserId(): string {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('You must be signed in to manage QR codes.');
    return userId;
  }

  static async createQRCode(qrData: QRCodeForm): Promise<string> {
    const ownerId = this.requireUserId();
    const documentRef = doc(db, QR_COLLECTION, qrData.id);

    try {
      await this.assertCustomPathAvailable(ownerId, qrData.customDomain, qrData.customSlug);
      // Firestore rules allow this write only for a new document. An existing
      // ID cannot be overwritten because immutable fields would change.
      await setDoc(documentRef, {
        ownerId,
        target: qrData.target,
        title: qrData.title || '',
        description: qrData.description || '',
        styleOptions: qrData.styleOptions || {},
        status: qrData.status || 'active',
        favorite: qrData.favorite || false,
        folderId: qrData.folderId || '',
        tags: qrData.tags || [],
        notes: qrData.notes || '',
        fallbackTarget: qrData.fallbackTarget || qrData.target,
        utm: qrData.utm || { enabled: false },
        schedule: qrData.schedule || { enabled: false, timezone: 'Europe/Berlin' },
        smartRules: qrData.smartRules || [],
        protection: qrData.protection || { enabled: false },
        landingPage: qrData.landingPage || { enabled: false },
        customDomain: qrData.customDomain || '',
        customSlug: qrData.customSlug || '',
        lastChangedBy: ownerId,
        changeComment: qrData.changeComment || 'QR-Code erstellt',
        health: { status: 'checking', consecutiveFailures: 0 },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        accessCount: 0,
        lastAccessed: null
      });
      return qrData.id;
    } catch (error) {
      console.error('Error creating QR code:', error);
      throw new Error('This QR code ID is unavailable. Generate a new ID and try again.');
    }
  }

  static async getAllQRCodes(): Promise<QRCode[]> {
    const ownerId = this.requireUserId();
    try {
      const ownerQuery = query(collection(db, QR_COLLECTION), where('ownerId', '==', ownerId));
      const querySnapshot = await getDocs(ownerQuery);

      return querySnapshot.docs.map((documentSnapshot) => this.mapQRCode(documentSnapshot.id, documentSnapshot.data()))
        .sort((first, second) => second.createdAt.getTime() - first.createdAt.getTime());
    } catch (error) {
      console.error('Error fetching QR codes:', error);
      throw new Error('Failed to fetch your QR codes.');
    }
  }

  static async getQRCode(id: string): Promise<QRCode | null> {
    const ownerId = this.requireUserId();
    try {
      const documentSnapshot = await getDoc(doc(db, QR_COLLECTION, id));
      if (!documentSnapshot.exists()) return null;

      const data = documentSnapshot.data();
      if (data.ownerId !== ownerId) return null;

      return this.mapQRCode(documentSnapshot.id, data);
    } catch (error) {
      if ((error as { code?: string }).code === 'permission-denied') return null;
      console.error('Error fetching QR code:', error);
      throw new Error('Failed to fetch QR code.');
    }
  }

  static async updateQRCode(id: string, updates: Partial<QRCodeForm>): Promise<void> {
    const ownerId = this.requireUserId();
    const documentRef = doc(db, QR_COLLECTION, id);

    try {
      const documentSnapshot = await getDoc(documentRef);
      if (!documentSnapshot.exists() || documentSnapshot.data().ownerId !== ownerId) {
        throw new Error('QR code not found');
      }
      await this.assertCustomPathAvailable(
        ownerId,
        typeof updates.customDomain === 'string' ? updates.customDomain : documentSnapshot.data().customDomain,
        typeof updates.customSlug === 'string' ? updates.customSlug : documentSnapshot.data().customSlug,
        id
      );

      const safeUpdates: Record<string, FieldValue | string | boolean | string[] | object | undefined> = {
        updatedAt: serverTimestamp(),
        lastChangedBy: ownerId
      };
      if (typeof updates.target === 'string') safeUpdates.target = updates.target;
      if (typeof updates.title === 'string') safeUpdates.title = updates.title;
      if (typeof updates.description === 'string') safeUpdates.description = updates.description;
      if (updates.styleOptions) safeUpdates.styleOptions = updates.styleOptions;
      if (updates.status) safeUpdates.status = updates.status;
      if (typeof updates.favorite === 'boolean') safeUpdates.favorite = updates.favorite;
      if (typeof updates.folderId === 'string') safeUpdates.folderId = updates.folderId;
      if (updates.tags) safeUpdates.tags = updates.tags;
      if (typeof updates.notes === 'string') safeUpdates.notes = updates.notes;
      if (typeof updates.fallbackTarget === 'string') safeUpdates.fallbackTarget = updates.fallbackTarget;
      if (updates.utm) safeUpdates.utm = updates.utm;
      if (updates.schedule) safeUpdates.schedule = updates.schedule;
      if (updates.smartRules) safeUpdates.smartRules = updates.smartRules;
      if (updates.protection) safeUpdates.protection = updates.protection;
      if (updates.landingPage) safeUpdates.landingPage = updates.landingPage;
      if (typeof updates.changeComment === 'string') safeUpdates.changeComment = updates.changeComment;
      if (typeof updates.customDomain === 'string') safeUpdates.customDomain = updates.customDomain;
      if (typeof updates.customSlug === 'string') safeUpdates.customSlug = updates.customSlug;

      await updateDoc(documentRef, safeUpdates);
    } catch (error) {
      console.error('Error updating QR code:', error);
      if (error instanceof Error && error.message === 'QR code not found') throw error;
      throw new Error('Failed to update QR code.');
    }
  }

  static async deleteQRCode(id: string): Promise<void> {
    const ownerId = this.requireUserId();
    const documentRef = doc(db, QR_COLLECTION, id);

    try {
      const documentSnapshot = await getDoc(documentRef);
      if (!documentSnapshot.exists() || documentSnapshot.data().ownerId !== ownerId) {
        throw new Error('QR code not found');
      }
      await deleteDoc(documentRef);
    } catch (error) {
      console.error('Error deleting QR code:', error);
      if (error instanceof Error && error.message === 'QR code not found') throw error;
      throw new Error('Failed to delete QR code.');
    }
  }

  static async getQRCodeStats(): Promise<QRCodeStats> {
    const ownerId = this.requireUserId();
    try {
      const ownerQuery = query(collection(db, QR_COLLECTION), where('ownerId', '==', ownerId));
      const querySnapshot = await getDocs(ownerQuery);
      let totalAccesses = 0;
      let recentAccesses = 0;
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

      querySnapshot.forEach((documentSnapshot) => {
        const data = documentSnapshot.data();
        const accessCount = data.accessCount || 0;
        totalAccesses += accessCount;
        const lastAccessed = data.lastAccessed?.toDate();
        if (lastAccessed && lastAccessed > oneDayAgo) recentAccesses += accessCount;
      });

      return { totalCodes: querySnapshot.size, totalAccesses, recentAccesses };
    } catch (error) {
      console.error('Error fetching QR code stats:', error);
      throw new Error('Failed to fetch your statistics.');
    }
  }

  static generateQRId(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let index = 0; index < 8; index += 1) result += chars.charAt(Math.floor(Math.random() * chars.length));
    return result;
  }

  private static async assertCustomPathAvailable(ownerId: string, domain?: string, slug?: string, currentId?: string): Promise<void> {
    if (!domain || !slug) return;
    const ownerQuery = query(collection(db, QR_COLLECTION), where('ownerId', '==', ownerId));
    const snapshot = await getDocs(ownerQuery);
    if (snapshot.docs.some(item => item.id !== currentId && item.data().customDomain === domain && item.data().customSlug === slug)) {
      throw new Error('Dieser Pfad wird auf der gewählten Domain bereits verwendet.');
    }
  }

  private static mapQRCode(id: string, data: Record<string, any>): QRCode {
    const health = data.health ? {
      ...data.health,
      checkedAt: data.health.checkedAt?.toDate?.() || undefined
    } : undefined;
    return {
      id,
      ownerId: data.ownerId,
      target: data.target,
      title: data.title || '',
      description: data.description || '',
      styleOptions: data.styleOptions || {},
      createdAt: data.createdAt?.toDate?.() || new Date(),
      updatedAt: data.updatedAt?.toDate?.(),
      lastAccessed: data.lastAccessed?.toDate?.(),
      accessCount: data.accessCount || 0,
      status: data.status || 'active',
      favorite: Boolean(data.favorite),
      folderId: data.folderId || '',
      tags: Array.isArray(data.tags) ? data.tags : [],
      notes: data.notes || '',
      fallbackTarget: data.fallbackTarget || data.target,
      utm: data.utm || { enabled: false },
      schedule: data.schedule || { enabled: false, timezone: 'Europe/Berlin' },
      smartRules: Array.isArray(data.smartRules) ? data.smartRules : [],
      protection: data.protection || { enabled: false },
      landingPage: data.landingPage || { enabled: false },
      health,
      lastChangedBy: data.lastChangedBy,
      changeComment: data.changeComment
      ,customDomain: data.customDomain || ''
      ,customSlug: data.customSlug || ''
    };
  }
}
